import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Sparkles, Scissors, ArrowDown, ArrowUp, Layers } from "lucide-react";
import { CardImage } from "./CardImage";

/**
 * Multi-step animated card-drawing ritual for the Vidente Virtual.
 *
 * Flow:
 *   1. choose-deck   — pick Tarot / Baraja Española / Runas
 *   2. shuffle       — visible shuffling animation (~3s)
 *   3. cut           — user drags a divider to cut the deck
 *   4. choose-side   — user picks top or bottom half
 *   5. revealing     — backend call in flight (busy spinner)
 *   6. reveal        — 9 cards fan out one by one with stagger
 *
 * Props:
 *   open       (bool)
 *   onClose    () => void
 *   onSubmit   ({ phase_id, cut_position, side }) => Promise<{ phase, reply_text }>
 *   busy       (bool) — externally triggered busy state (during onSubmit)
 *   forcedDeck (string | null) — if Cristopher already picked the phase, skip step 1
 */
const DECKS = [
  {
    id: "tarot",
    label: "Tarot",
    sub: "78 arcanos — el lenguaje clásico de los videntes",
    glow: "from-purple-700/40 via-[#d4af37]/30 to-purple-900/40",
    icon: "🎴",
  },
  {
    id: "spanish_cards",
    label: "Baraja Española",
    sub: "48 cartas — oros, copas, espadas, bastos",
    glow: "from-amber-700/40 via-[#d4af37]/30 to-red-900/40",
    icon: "🃏",
  },
  {
    id: "runes",
    label: "Runas Nórdicas",
    sub: "24 runas ancestrales del Futhark",
    glow: "from-blue-900/40 via-[#d4af37]/30 to-indigo-900/40",
    icon: "ᚱ",
  },
];

export function CardDrawRitual({ open, onClose, onSubmit, busy, forcedDeck = null }) {
  const [step, setStep] = useState(forcedDeck ? "shuffle" : "choose-deck");
  const [chosenDeck, setChosenDeck] = useState(forcedDeck);
  const [cutPosition, setCutPosition] = useState(50);
  const [side, setSide] = useState(null);
  const [revealed, setRevealed] = useState(null); // { phase, reply_text }
  const submittedRef = useRef(false);

  // Reset internal state every time the modal reopens.
  useEffect(() => {
    if (open) {
      submittedRef.current = false;
      setStep(forcedDeck ? "shuffle" : "choose-deck");
      setChosenDeck(forcedDeck);
      setCutPosition(50);
      setSide(null);
      setRevealed(null);
    }
  }, [open, forcedDeck]);

  // Auto-advance shuffle step after ~3s
  useEffect(() => {
    if (step !== "shuffle") return undefined;
    const t = setTimeout(() => setStep("cut"), 3000);
    return () => clearTimeout(t);
  }, [step]);

  const handleDeckPick = (deckId) => {
    setChosenDeck(deckId);
    setStep("shuffle");
  };

  const handleSidePick = async (s) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSide(s);
    setStep("revealing");
    try {
      const result = await onSubmit({
        phase_id: chosenDeck,
        cut_position: cutPosition,
        side: s,
      });
      if (result?.phase) {
        setRevealed(result);
        setStep("reveal");
      } else {
        // Parent already showed a toast — just close.
        onClose();
      }
    } catch {
      onClose();
    }
  };

  if (!open) return null;

  const deckMeta = DECKS.find((d) => d.id === chosenDeck) || DECKS[0];

  return (
    <AnimatePresence>
      <motion.div
        key="ritual-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center px-4 py-6"
        data-testid="card-ritual-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-2xl rounded-3xl bg-gradient-to-br from-[#0a0510] via-[#150820] to-black border border-[#d4af37]/30 shadow-[0_0_80px_rgba(212,175,55,0.25)] overflow-hidden"
        >
          {/* close (only when not in revealing/reveal so user can't bail mid-call) */}
          {step !== "revealing" && (
            <button
              onClick={onClose}
              data-testid="ritual-close"
              className="absolute top-3 right-3 z-10 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="relative px-6 py-8 sm:px-10 sm:py-10 min-h-[420px]">
            {/* Step 1: choose deck */}
            {step === "choose-deck" && (
              <motion.div
                key="step-deck"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
                data-testid="ritual-step-choose-deck"
              >
                <Sparkles className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl sm:text-3xl text-white mb-1">
                  Elige tu instrumento
                </h2>
                <p className="text-sm text-neutral-400 mb-8">
                  ¿Qué mazo quieres que voltee Cristopher por ti?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {DECKS.map((d) => (
                    <motion.button
                      key={d.id}
                      data-testid={`ritual-deck-${d.id}`}
                      whileHover={{ scale: 1.04, y: -3 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleDeckPick(d.id)}
                      className={`relative group rounded-2xl border border-[#d4af37]/30 bg-gradient-to-br ${d.glow} p-5 text-center overflow-hidden hover:border-[#d4af37]`}
                    >
                      <div className="text-5xl mb-2 drop-shadow-[0_0_10px_rgba(212,175,55,0.4)]">
                        {d.icon}
                      </div>
                      <div className="font-serif text-lg text-white">{d.label}</div>
                      <div className="text-[11px] text-neutral-300 mt-1">{d.sub}</div>
                      <span className="absolute inset-0 rounded-2xl border border-[#d4af37]/0 group-hover:border-[#d4af37]/70 transition pointer-events-none" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: shuffle animation */}
            {step === "shuffle" && (
              <motion.div
                key="step-shuffle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
                data-testid="ritual-step-shuffle"
              >
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#d4af37] mb-3">
                  {deckMeta.label}
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-white mb-6">
                  Cristopher está barajando...
                </h2>
                <ShuffleAnimation />
                <p className="text-xs text-neutral-400 mt-6 max-w-sm mx-auto">
                  Respira. Concéntrate en tu pregunta mientras las cartas se mezclan con tu energía.
                </p>
              </motion.div>
            )}

            {/* Step 3: cut */}
            {step === "cut" && (
              <motion.div
                key="step-cut"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
                data-testid="ritual-step-cut"
              >
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#d4af37] mb-3">
                  {deckMeta.label}
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-white mb-2 flex items-center justify-center gap-2">
                  <Scissors className="w-6 h-6 text-[#d4af37]" /> Parte el mazo
                </h2>
                <p className="text-sm text-neutral-400 mb-8 max-w-sm mx-auto">
                  Desliza el corte donde sientas que es el momento exacto. Tu mano elige.
                </p>
                <CutInteraction position={cutPosition} onChange={setCutPosition} />
                <button
                  data-testid="ritual-cut-confirm"
                  onClick={() => setStep("choose-side")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#d4af37] text-black font-semibold uppercase tracking-[0.2em] text-xs hover:bg-[#e6c350] shadow-[0_0_24px_rgba(212,175,55,0.45)]"
                >
                  Cortar aquí
                </button>
              </motion.div>
            )}

            {/* Step 4: choose side */}
            {step === "choose-side" && (
              <motion.div
                key="step-side"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
                data-testid="ritual-step-side"
              >
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#d4af37] mb-3">
                  {deckMeta.label}
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-white mb-2">
                  ¿Qué lado del mazo?
                </h2>
                <p className="text-sm text-neutral-400 mb-8 max-w-sm mx-auto">
                  Cortaste en {cutPosition}%. Ahora elige el lado que tu intuición indica.
                </p>
                <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                  <motion.button
                    data-testid="ritual-side-top"
                    whileHover={{ scale: 1.04, y: -3 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSidePick("top")}
                    className="relative rounded-2xl border border-[#d4af37]/40 bg-gradient-to-br from-purple-700/20 to-black p-6 hover:border-[#d4af37] flex flex-col items-center gap-2"
                  >
                    <ArrowUp className="w-6 h-6 text-[#d4af37]" />
                    <span className="font-serif text-lg text-white">Lado de arriba</span>
                    <span className="text-[11px] text-neutral-400">Lo que sale a la luz</span>
                  </motion.button>
                  <motion.button
                    data-testid="ritual-side-bottom"
                    whileHover={{ scale: 1.04, y: -3 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSidePick("bottom")}
                    className="relative rounded-2xl border border-[#d4af37]/40 bg-gradient-to-br from-amber-700/20 to-black p-6 hover:border-[#d4af37] flex flex-col items-center gap-2"
                  >
                    <ArrowDown className="w-6 h-6 text-[#d4af37]" />
                    <span className="font-serif text-lg text-white">Lado de abajo</span>
                    <span className="text-[11px] text-neutral-400">Lo que estaba oculto</span>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Step 5: revealing (loading) */}
            {step === "revealing" && (
              <motion.div
                key="step-revealing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-10"
                data-testid="ritual-step-revealing"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.06, 1],
                    boxShadow: [
                      "0 0 40px rgba(212,175,55,0.35)",
                      "0 0 90px rgba(168,85,247,0.55)",
                      "0 0 40px rgba(212,175,55,0.35)",
                    ],
                  }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                  className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-[#d4af37]/30 via-purple-700/30 to-black/40 border border-[#d4af37]/50 flex items-center justify-center mb-6"
                >
                  <Loader2 className="w-12 h-12 text-[#d4af37] animate-spin" />
                </motion.div>
                <h2 className="font-serif text-xl text-white mb-2">
                  Cristopher está volteando tus 9 cartas...
                </h2>
                <p className="text-sm text-neutral-400 max-w-sm mx-auto">
                  Las cartas que tu corte eligió. No las he visto todavía — abramos los ojos juntos.
                </p>
              </motion.div>
            )}

            {/* Step 6: reveal — 9 cards */}
            {step === "reveal" && revealed && (
              <motion.div
                key="step-reveal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
                data-testid="ritual-step-reveal"
              >
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#d4af37] mb-3">
                  Tirada de {revealed.phase?.label}
                </p>
                <NineCardsFan cards={revealed.phase?.cards || []} />
                <button
                  data-testid="ritual-finish"
                  onClick={onClose}
                  disabled={busy}
                  className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#d4af37]/20 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/30 uppercase tracking-[0.2em] text-xs font-semibold"
                >
                  <Layers className="w-3.5 h-3.5" /> Escuchar la lectura
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Visible shuffle animation — 6 stacked cards swap places repeatedly. */
function ShuffleAnimation() {
  const cards = [0, 1, 2, 3, 4, 5];
  return (
    <div className="relative h-44 w-full max-w-xs mx-auto" data-testid="shuffle-animation">
      {cards.map((i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 w-20 h-32 rounded-lg border border-[#d4af37]/40 bg-gradient-to-br from-[#1a0a1f] via-[#0a0510] to-black shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
          style={{ marginLeft: -40, marginTop: -64 }}
          animate={{
            x: [0, (i % 2 === 0 ? -1 : 1) * (40 + i * 6), (i % 2 === 0 ? 1 : -1) * (30 + i * 4), 0],
            y: [0, -10 - i * 4, 6 + i * 3, 0],
            rotate: [0, (i % 2 === 0 ? -12 : 12), (i % 2 === 0 ? 8 : -8), 0],
            zIndex: [i, 5 - i, i, i],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.08,
          }}
        >
          <div className="absolute inset-1.5 rounded border border-[#d4af37]/30 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#d4af37]/60" />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/** Interactive deck-cutting widget. */
function CutInteraction({ position, onChange }) {
  return (
    <div className="max-w-md mx-auto">
      <div className="relative h-40 rounded-2xl bg-gradient-to-b from-[#1a0a1f] via-[#0a0510] to-black border border-[#d4af37]/30 overflow-hidden">
        {/* Stack of card-back rectangles (visual stack) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 px-3 py-2">
          {[...Array(14)].map((_, i) => (
            <div
              key={i}
              className="w-full h-1.5 rounded-sm bg-gradient-to-r from-[#d4af37]/15 via-[#d4af37]/35 to-[#d4af37]/15 border border-[#d4af37]/20"
            />
          ))}
        </div>
        {/* Cut line indicator */}
        <motion.div
          animate={{ top: `${position}%` }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="absolute left-0 right-0 h-0.5 bg-[#d4af37] shadow-[0_0_18px_rgba(212,175,55,0.85)] pointer-events-none"
          style={{ transform: "translateY(-50%)" }}
        >
          <Scissors className="absolute -left-2 -top-3 w-5 h-5 text-[#d4af37] drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
        </motion.div>
      </div>
      <input
        data-testid="ritual-cut-slider"
        type="range"
        min="5"
        max="95"
        value={position}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full mt-4 accent-[#d4af37]"
      />
      <div className="text-[11px] uppercase tracking-widest text-[#d4af37]/80 mt-1">
        Corte: {position}%
      </div>
    </div>
  );
}

/** Reveals 9 cards in a fan with staggered animation. */
function NineCardsFan({ cards }) {
  if (!cards?.length) return null;
  return (
    <div
      className="relative flex flex-wrap items-center justify-center gap-2 sm:gap-3 py-2"
      data-testid="nine-cards-fan"
    >
      {cards.map((c, i) => (
        <motion.div
          key={i}
          initial={{ rotateY: 180, y: 30, opacity: 0 }}
          animate={{ rotateY: 0, y: 0, opacity: 1 }}
          transition={{
            duration: 0.65,
            delay: 0.1 + i * 0.18,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{ transformStyle: "preserve-3d" }}
        >
          <CardImage card={c} size="sm" delay={0} />
        </motion.div>
      ))}
    </div>
  );
}
